import { cn } from '@/lib/utils';
import { DatepickerAlt } from '@chiroup/components/datepicker/DatepickerAlt';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement } from 'react';
import Alert from './common/Alert';

type Props = {
  selectedDate?: Dayjs;
  setSelectedDate: (date: Dayjs) => void;
  onChange: (value: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  }) => void;
  primaryColor: string;
  onSelectDate: (date: string) => void;
  selectedDay?: string;
  onChangeMonth: (year: string, month: string) => void;
  dottedDays: { [key: number]: boolean };
  loading: boolean;
  availableTimes: {
    providerId: string;
    providerName?: string;
    profileImage?: string | null;
    times: {
      date: string;
      startTime: Dayjs;
      endTime: Dayjs;
      provider: string;
      duration: number;
    }[];
  }[];
  viewDate: dayjs.Dayjs;
  setViewDate: (date: dayjs.Dayjs) => void;
  title?: ReactElement;
  className?: string;
  compact?: boolean;
  error?: string | null;
  selectedTimeSlot?: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  } | null;
};

const ScheduleTime: React.FC<Props> = ({
  selectedDate,
  setSelectedDate,
  onChange,
  primaryColor,
  onSelectDate,
  selectedDay,
  onChangeMonth,
  dottedDays,
  loading,
  availableTimes,
  viewDate,
  setViewDate,
  title = <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Select a time</h3>,
  className,
  compact = false,
  error,
  selectedTimeSlot,
}) => {
  const isSlotSelected = (time: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
  }) =>
    selectedTimeSlot &&
    selectedTimeSlot.date === time.date &&
    selectedTimeSlot.startTime.isSame(time.startTime) &&
    selectedTimeSlot.provider === time.provider;

  return (
    <div className={className}>
      <div className="flex flex-col gap-5" id="time">
        {title}
        {!!error && <Alert title="Error" error={error} />}
        <div className="portal-card p-4">
          <DatepickerAlt
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onChange={onSelectDate}
            onChangeMonth={onChangeMonth}
            primaryColor={primaryColor}
            dottedDays={dottedDays}
            loading={loading}
            viewDate={viewDate}
            setViewDate={setViewDate}
          />
        </div>
        {!!selectedDay && (
          <div className="mt-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">
              {selectedDay}
            </h3>
            <div className="flex flex-col gap-2.5">
              {availableTimes?.map((provider, i) => {
                return (
                  <div key={i} className="portal-card p-4">
                    <div className="flex gap-x-3 items-start">
                      {compact ? null : provider.profileImage ? (
                        <img
                          className="h-11 w-11 flex-none rounded-xl bg-gray-50 object-cover"
                          src={provider.profileImage}
                          alt={provider.providerName}
                        />
                      ) : (
                        <span
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl flex-none text-white font-bold text-sm"
                          style={{ backgroundColor: primaryColor || 'var(--primary-color)' }}
                        >
                          {provider.providerName?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                      )}
                      <div className="min-w-0 flex-auto">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {provider.providerName}
                        </span>
                        <div className="mt-2.5 flex flex-row gap-2 flex-wrap">
                          {provider.times.map((time, j) => (
                            <button
                              key={j}
                              type="button"
                              className={cn(
                                'inline-flex items-center rounded-xl px-3 py-2 text-xs font-semibold transition-colors',
                                isSlotSelected(time)
                                  ? 'text-white'
                                  : // Unselected chips were `text-gray-700` on a
                                    // transparent background, which read as dark
                                    // text on a dark card in dark mode — chips
                                    // were nearly invisible. Mirror the light/dark
                                    // pattern used elsewhere in the portal.
                                    'text-gray-700 ring-1 ring-inset ring-gray-200 hover:ring-gray-300 dark:text-gray-100 dark:ring-white/15 dark:hover:ring-white/25'
                              )}
                              style={
                                isSlotSelected(time)
                                  ? { backgroundColor: primaryColor || 'var(--primary-color)' }
                                  : undefined
                              }
                              onClick={() => onChange(time)}
                            >
                              {time.startTime.format('h:mm A')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleTime;
